import { Text, Stack, Box } from 'braid-design-system';
import React from 'react';

import { StaticPageProvider, TextLink } from 'src/components';
import { TRACKING_CODES } from 'src/shared/constant';
import { useLocalized } from 'src/shared/hooks';
import type { PageProps } from 'src/shared/types';

import {
  IntroBanner,
  ProductPromos,
  PromoTiles,
  PromoFooter,
} from './components';
import CloserLookImageNZ from './images/closer-look-nz.png';
import CloserLookImage from './images/closer-look.png';
import ShortlistImageNZ from './images/easy-shortlisting-nz.png';
import ShortlistImage from './images/easy-shortlisting.png';
import PeopleIcon from './images/icon1.png';
import VerifiedIcon from './images/icon2.png';
import ManageIcon from './images/icon3.png';
import LoopImage from './images/keep-people-in-the-loop.png';
import SortImageNZ from './images/sort-people-nz.png';
import SortImage from './images/sort-people.png';

const StaticPageProps: PageProps = {
  title: 'Ad Centre',
  returnUrl: '/products/adcentre/',
  currentPage: 'adcentre',
  eventName: 'adcentre-viewed',
  siteSection: 'products',
  activeTab: 'Products',
  activeSubTab: 'Candidate management',
};

export default () => {
  const PromoTileData = {
    AU: [
      {
        icon: PeopleIcon,
        text: 'See who’s most suited to your job ad first, so you can identify the best candidates to connect with',
      },
      {
        icon: VerifiedIcon,
        text: 'Quickly see verified candidate credentials so you can be confident about who is qualified',
      },
      {
        icon: ManageIcon,
        text: 'Easily manage your shortlist and keep candidates in the loop',
      },
    ],
    NZ: [
      {
        icon: PeopleIcon,
        text: 'See who’s most suited to your job ad first, so you can identify the best candidates to connect with',
      },
      {
        icon: VerifiedIcon,
        text: 'Quickly see verified candidate credentials so you can be confident about who is qualified',
      },
      {
        icon: ManageIcon,
        text: 'Easily manage your shortlist and keep candidates in the loop',
      },
    ],
  };
  const renderSeekInfo = (
    <>
      {' '}
      Plus, you can confirm candidate credentials &ndash; such as licences or
      tertiary qualifications &ndash; with{' '}
      <TextLink
        href="https://seekpass.co"
        target="_blank"
        tracking={TRACKING_CODES.certsy_link_clicked}
        product="other"
      >
        SEEK Pass
      </TextLink>
      .
    </>
  );

  return (
    <StaticPageProvider {...StaticPageProps}>
      <Box background="surface">
        <Stack space="none">
          <IntroBanner
            title="All the right tools to help you hire"
            text={`
              A SEEK job ad is more than an ad. It comes with tools to help you
              assess, select, shortlist and contact candidates as you work through the
              hiring process. Plus, it shows you the most relevant candidates first,
              so you can find the right person for the job faster.
            `}
          />
          <PromoTiles
            title="Finding the best person for the job, made easier"
            tiles={useLocalized(PromoTileData)}
          />
          <ProductPromos
            title="Focus on the right candidates from the start"
            icon={useLocalized({
              NZ: SortImageNZ,
              AU: SortImage,
            })}
            tone="purple"
            reverse
          >
            <Text tone="secondary">
              We show you the most relevant people first, and help you filter to
              those that best suit the role. Use screening questions to
              customise your filters and set your &ldquo;must-have&rdquo;
              requirements to help you screen and shortlist candidate faster.
              {useLocalized({
                AU: renderSeekInfo,
                NZ: renderSeekInfo,
              })}
            </Text>
          </ProductPromos>
          <ProductPromos
            title="Get a clearer picture of the best people for the job"
            icon={useLocalized({
              NZ: CloserLookImageNZ,
              AU: CloserLookImage,
            })}
          >
            <Text tone="secondary">
              We&apos;ll highlight a candidate&apos;s skills and qualities that
              match your job ad, so you can quickly see if they&apos;re a good
              fit for the role. Then, you can check their work history, resumé
              and cover letter to get the detail you need.
            </Text>
          </ProductPromos>
          <ProductPromos
            title="Assemble your shortlist quickly"
            icon={useLocalized({
              NZ: ShortlistImageNZ,
              AU: ShortlistImage,
            })}
            tone="yellow"
            reverse
          >
            <Text tone="secondary">
              Organise candidates quickly with one-click sorting into your
              shortlist, leaving you more time to prepare for the next phase.
            </Text>
          </ProductPromos>
          <ProductPromos
            title="Easily keep in contact with candidates"
            icon={LoopImage}
          >
            <Text tone="secondary">
              A SEEK job ad makes it easy to keep track of who you&apos;ve
              contacted. Plus, bulk emailing lets you contact multiple
              candidates at once to keep them updated on where they&apos;re at
              in the process.
            </Text>
          </ProductPromos>
          <PromoFooter title="Create your job ad now" />
        </Stack>
      </Box>
    </StaticPageProvider>
  );
};
